import React from 'react';
import { Routes, Route } from 'react-router-dom';
import PermissionProtectedRoute from '../auth/PermissionProtectedRoute';
import WarehouseTransferList from './WarehouseTransferList';
import WarehouseList from './WarehouseList';
import WarehouseForm from './WarehouseForm';
import WarehouseTransfer from './WarehouseTransfer';
import InventoryEvents from '../../pages/InventoryEvents';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventIcon from '@mui/icons-material/Event';

// مكون للخبز (Breadcrumbs)
const WarehouseBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);
  const { id } = useParams<{ id: string }>();

  // تحديد عنوان الصفحة والفتات الخبز حسب المسار
  let pageTitle = "المستودعات";
  let breadcrumbs = [
    <Link
      key="home"
      component={RouterLink}
      to="/"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
      الرئيسية
    </Link>,
    <Link
      key="warehouses"
      component={RouterLink}
      to="/warehouses"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <WarehouseIcon sx={{ mr: 0.5 }} fontSize="inherit" />
      المستودعات
    </Link>
  ];

  if (pathnames.includes('list')) {
    if (pathnames.includes('add')) {
      pageTitle = "إضافة مستودع جديد";
      breadcrumbs.push(
        <Link
          key="list"
          component={RouterLink}
          to="/warehouses/list"
        >
          قائمة المستودعات
        </Link>,
        <Typography key="add" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <AddIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          إضافة مستودع جديد
        </Typography>
      );
    } else if (pathnames.includes('edit')) {
      pageTitle = "تعديل مستودع";
      breadcrumbs.push(
        <Link
          key="list"
          component={RouterLink}
          to="/warehouses/list"
        >
          قائمة المستودعات
        </Link>,
        <Typography key="edit" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          تعديل مستودع {id ? `#${id}` : ''}
        </Typography>
      );
    } else {
      pageTitle = "قائمة المستودعات";
      breadcrumbs.push(
        <Typography key="list" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ListIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          قائمة المستودعات
        </Typography>
      );
    }
  } else if (pathnames.includes('inventory-events')) {
    pageTitle = "أحداث معاملات المخزون";
    breadcrumbs.push(
      <Typography key="inventory-events" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
        <EventIcon sx={{ mr: 0.5 }} fontSize="inherit" />
        أحداث معاملات المخزون
      </Typography>
    );
  } else if (pathnames.includes('transfer-list')) {
    pageTitle = "قائمة التحويلات";
    breadcrumbs.push(
      <Typography key="transfer-list" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
        <SwapHorizIcon sx={{ mr: 0.5 }} fontSize="inherit" />
        قائمة التحويلات
      </Typography>
    );
  } else if (pathnames.includes('transfer')) {
    if (pathnames.includes('edit')) {
      pageTitle = "تعديل تحويل";
      breadcrumbs.push(
        <Link
          key="transfer-list"
          component={RouterLink}
          to="/warehouses/transfer-list"
        >
          قائمة التحويلات
        </Link>,
        <Typography key="edit" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          تعديل تحويل {id ? `#${id}` : ''}
        </Typography>
      );
    } else {
      pageTitle = "إنشاء تحويل جديد";
      breadcrumbs.push(
        <Typography key="new-transfer" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <AddIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          إنشاء تحويل جديد
        </Typography>
      );
    }
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom align="right">
        {pageTitle}
      </Typography>
      <Breadcrumbs separator="›" aria-label="breadcrumb" dir="rtl">
        {breadcrumbs}
      </Breadcrumbs>
    </Box>
  );
};

// مكون الصفحة الرئيسية للمستودعات
const WarehouseDashboard: React.FC = () => {
  return (
    <Box>
      <WarehouseBreadcrumbs />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', p: 3 }}>
        <Typography variant="h6" align="center">
          مرحبًا بك في قسم المستودعات
        </Typography>
        <Typography align="center">
          من هنا يمكنك إدارة المستودعات والتحويلات بين المستودعات
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Link component={RouterLink} to="/warehouses/list" underline="none">
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                width: 200,
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              <ListIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                قائمة المستودعات
              </Typography>
            </Box>
          </Link>
          <Link component={RouterLink} to="/warehouses/transfer-list" underline="none">
            <Box
              sx={{
                bgcolor: 'info.main',
                color: 'white',
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                width: 200,
                '&:hover': {
                  bgcolor: 'info.dark',
                }
              }}
            >
              <SwapHorizIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                قائمة التحويلات
              </Typography>
            </Box>
          </Link>
          <Link component={RouterLink} to="/warehouses/transfer" underline="none">
            <Box
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
                width: 200,
                '&:hover': {
                  bgcolor: 'secondary.dark',
                }
              }}
            >
              <AddIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                إنشاء تحويل جديد
              </Typography>
            </Box>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

// مكون مسارات المستودعات
const WarehouseRoutes: React.FC = () => {
  return (
    <Routes>
      <Route index element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="list" actionId="view_page">
          <WarehouseDashboard />
        </PermissionProtectedRoute>
      } />
      
      {/* قائمة المستودعات */}
      <Route path="list" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="list" actionId="view_page">
          <Box>
            <WarehouseBreadcrumbs />
            <WarehouseList />
          </Box>
        </PermissionProtectedRoute>
      } />
      
      {/* إضافة مستودع جديد */}
      <Route path="list/add" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="list" actionId="create">
          <Box>
            <WarehouseBreadcrumbs />
            <WarehouseForm />
          </Box>
        </PermissionProtectedRoute>
      } />
      
      {/* تعديل مستودع */}
      <Route path="list/edit/:id" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="list" actionId="edit">
          <Box>
            <WarehouseBreadcrumbs />
            <WarehouseForm />
          </Box>
        </PermissionProtectedRoute>
      } />
      
      {/* قائمة التحويلات */}
      <Route path="transfer-list" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="transfers" actionId="view_page">
          <Box>
            <WarehouseBreadcrumbs />
            <WarehouseTransferList />
          </Box>
        </PermissionProtectedRoute>
      } />
      
      {/* إنشاء تحويل جديد */}
      <Route path="transfer" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="new-transfer" actionId="view_page">
          <Box>
            <WarehouseBreadcrumbs />
            <WarehouseTransfer />
          </Box>
        </PermissionProtectedRoute>
      } />
      
      {/* تعديل تحويل */}
      <Route path="transfer/edit/:transferId" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="transfers" actionId="edit">
          <Box>
            <WarehouseBreadcrumbs />
            <WarehouseTransfer />
          </Box>
        </PermissionProtectedRoute>
      } />
      
      {/* أحداث معاملات المخزون */}
      <Route path="inventory-events" element={
        <PermissionProtectedRoute sectionId="warehouses" pageId="inventory-events" actionId="view_page">
          <Box>
            <WarehouseBreadcrumbs />
            <InventoryEvents />
          </Box>
        </PermissionProtectedRoute>
      } />
    </Routes>
  );
};

export default WarehouseRoutes;